import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, 
  Calendar, 
  Sparkles, 
  BarChart3, 
  Cloud,
  Users,
  DollarSign,
  Zap,
  ArrowRight,
  Building2,
  ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const LandingPage = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
                Run your entire property from{' '}
                <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                  one simple dashboard
                </span>
              </h1>
              <p className="text-xl text-slate-600 mb-8">
                Keyless PMS brings reservations, housekeeping, maintenance, and reporting together in one cloud platform. Built for independent hotels, B&Bs, and small hotel groups.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Link to="/signup">
                  <Button size="lg" className="bg-gradient-to-r from-cyan-600 to-blue-600 w-full sm:w-auto">
                    Start Free Trial
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <a href="mailto:sales@keylesspms.com?subject=Demo%20Request">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    Book a Demo
                  </Button>
                </a>
              </div>
              <p className="text-sm text-slate-500">
                Simple per-room pricing. No setup fees. Cancel anytime.
              </p>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl p-8 shadow-2xl">
                <div className="bg-white rounded-lg p-6 space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b">
                    <span className="text-sm font-medium text-slate-600">Today's Overview</span>
                    <span className="text-sm text-cyan-600 font-medium">Live</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-4">
                      <div className="text-3xl font-bold text-cyan-700">78%</div>
                      <div className="text-xs text-slate-600 mt-1">Occupancy</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4">
                      <div className="text-3xl font-bold text-blue-700">12</div>
                      <div className="text-xs text-slate-600 mt-1">Check-ins</div>
                    </div>
                  </div>
                  <div className="space-y-2 pt-2">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-slate-600">8 rooms ready</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      <span className="text-slate-600">3 rooms in progress</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-600 mb-8">Designed for independent hotels and growing brands</p>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
            <Building2 size={40} className="text-slate-400" />
            <Building2 size={40} className="text-slate-400" />
            <Building2 size={40} className="text-slate-400" />
            <Building2 size={40} className="text-slate-400" />
            <Building2 size={40} className="text-slate-400" />
          </div>
        </div>
      </section>

      {/* Why Keyless PMS */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Keyless PMS?</h2>
            <p className="text-xl text-slate-600">Everything you need in one simple platform</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-cyan-200 transition">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                  <Calendar className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-3">All your operations in one place</h3>
                <p className="text-slate-600">
                  Manage reservations, view your calendar, track housekeeping, handle maintenance, and run reports from a single system. No more switching between multiple tools or spreadsheets.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-cyan-200 transition">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
                  <Zap className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-3">Built for busy front desks</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <Check size={20} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span>Check guests in or out with 2 clicks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={20} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span>See today's arrivals, departures, and tasks at a glance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={20} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span>Simple interface your team can learn in a day</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-cyan-200 transition">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-4">
                  <DollarSign className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-3">Fair pricing that scales with you</h3>
                <p className="text-slate-600">
                  Pay only for the rooms you have. Add more properties as you grow. Manage everything from one login with role-based access for your team.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Everything you need to run your property</h2>
            <p className="text-xl text-slate-600">Powerful features that make operations effortless</p>
          </div>

          <div className="space-y-24">
            {/* Feature 1 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium mb-4">
                  <Calendar size={16} />
                  Reservations & Front Desk
                </div>
                <h3 className="text-3xl font-bold mb-4">Handle bookings from anywhere</h3>
                <p className="text-lg text-slate-600 mb-6">
                  Your interactive calendar shows availability, arrivals, and departures in real time. Create bookings from phone calls, walk-ins, or online requests in seconds.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">Visual tape chart calendar with drag-and-drop</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">Quick check-in and check-out workflows</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">Group bookings and multi-room reservations</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow-xl p-8">
                <div className="aspect-video bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg flex items-center justify-center">
                  <Calendar size={64} className="text-cyan-600" />
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 bg-white rounded-xl shadow-xl p-8">
                <div className="aspect-video bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg flex items-center justify-center">
                  <Sparkles size={64} className="text-blue-600" />
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                  <Sparkles size={16} />
                  Housekeeping & Maintenance
                </div>
                <h3 className="text-3xl font-bold mb-4">Keep rooms ready and issues resolved</h3>
                <p className="text-lg text-slate-600 mb-6">
                  Track room status in real time. Generate daily housekeeping lists automatically. Create maintenance tickets and assign them to staff.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">Room status updates: Clean, Dirty, In-progress, Out of Order</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">Auto-generated housekeeping task lists</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">Maintenance work orders with priority levels</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
                  <BarChart3 size={16} />
                  Reporting & Forecasting
                </div>
                <h3 className="text-3xl font-bold mb-4">Understand your performance</h3>
                <p className="text-lg text-slate-600 mb-6">
                  See occupancy rates, revenue trends, and upcoming forecasts. Export data for your accountant or ownership reports.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="text-purple-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">Real-time occupancy and revenue dashboards</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="text-purple-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">30-60-90 day occupancy forecasting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="text-purple-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-slate-600">CSV exports for accounting and analysis</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow-xl p-8">
                <div className="aspect-video bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg flex items-center justify-center">
                  <BarChart3 size={64} className="text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Teaser */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Get started in three simple steps</h2>
            <p className="text-xl text-slate-600">From setup to daily operations in minutes</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Set up your property</h3>
              <p className="text-slate-600">
                Add your rooms, set your rates, configure taxes. The setup wizard guides you through everything in about 10 minutes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Run your day</h3>
              <p className="text-slate-600">
                Use the calendar to manage bookings and the Today view to see arrivals, departures, and tasks. Everything you need is right there.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">See the full picture</h3>
              <p className="text-slate-600">
                Track your occupancy, revenue, and upcoming forecast from the dashboard. Export reports when you need them.
              </p>
            </div>
          </div>

          <div className="text-center">
            <Link to="/how-it-works">
              <Button variant="outline" size="lg">
                Learn more about how it works
                <ChevronRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Simple, transparent pricing</h2>
            <p className="text-xl text-slate-600">Pricing starts per room, per month. No surprise fees.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="border-2 hover:border-cyan-200 transition">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold mb-2">Starter</h3>
                <p className="text-slate-600 mb-4">Guesthouses & small hotels (1-20 rooms)</p>
                <p className="text-slate-600 mb-6">
                  Everything you need to manage a single property: reservations, calendar, housekeeping, maintenance, and basic reports.
                </p>
                <Link to="/pricing">
                  <Button variant="outline" className="w-full">View full pricing</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 border-cyan-500 hover:border-cyan-600 transition relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-sm font-medium rounded-full">
                Most Popular
              </div>
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold mb-2">Pro</h3>
                <p className="text-slate-600 mb-4">Larger properties & small groups (21-100 rooms)</p>
                <p className="text-slate-600 mb-6">
                  All Starter features plus forecasting, exports, multi-property management, and staff scheduling.
                </p>
                <Link to="/pricing">
                  <Button className="w-full bg-gradient-to-r from-cyan-600 to-blue-600">View full pricing</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-cyan-200 transition">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
                <p className="text-slate-600 mb-4">Brands & portfolios (100+ rooms)</p>
                <p className="text-slate-600 mb-6">
                  Everything in Pro plus priority support, custom integrations, and dedicated account management.
                </p>
                <Link to="/pricing">
                  <Button variant="outline" className="w-full">View full pricing</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Frequently asked questions</h2>
          </div>

          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-2">Do you charge per user or per room?</h3>
                <p className="text-slate-600">
                  We charge per room, not per user. Invite as many staff members as you need at no extra cost. You only pay based on the number of rooms at your property.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-2">Do I need to install anything?</h3>
                <p className="text-slate-600">
                  No. Keyless PMS runs entirely in your web browser. No downloads, no servers to maintain. Just log in from any computer or tablet.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-2">Is there a free trial?</h3>
                <p className="text-slate-600">
                  Yes. You get a 14-day free trial with full access to all features. No credit card required to start. You can add billing details later if you decide to continue.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-2">Can I import data from my current system?</h3>
                <p className="text-slate-600">
                  We can help you migrate your guest data and reservation history. Contact our team during signup, and we'll guide you through the import process.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to simplify your property operations?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join hotels and guesthouses that run their entire operation on Keyless PMS. Start your free trial today—no credit card required, no installation needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link to="/signup">
              <Button size="lg" className="bg-white text-cyan-700 hover:bg-slate-100 w-full sm:w-auto">
                Start Free Trial
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <a href="mailto:sales@keylesspms.com?subject=Demo%20Request">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                Book a Demo
              </Button>
            </a>
          </div>
          <p className="text-sm opacity-75">
            14-day free trial • No credit card required • Cancel anytime
          </p>
        </div>
      </section>
    </>
  );
};

export default LandingPage;
